/**
 * Bounded, escalating recovery from Vite's `vite:preloadError`.
 *
 * Shared by `apps/ops` and `apps/learn`: both are installable PWAs served from
 * the same auto-deploy pipeline, so both hit the identical failure and both used
 * to carry the identical unguarded one-liner.
 *
 * ─── THE LOOP THIS GUARDS AGAINST — DO NOT REMOVE THE COUNTER ───
 *
 * Every deploy rewrites the hashed chunk filenames. That is not an edge case,
 * it is what a merge to `main` does: PR #362 turned `cohort-BwZrxpdM.js` into
 * `cohort-BbJQ01nu.js`, and the `@convex/lib/cohortClients` alias it added
 * reshaped the module graph on top of that. Any tab opened before the deploy is
 * still holding the OLD `index.html`, so the moment the user navigates to a lazy
 * route it requests a chunk name that no longer exists and Vite fires
 * `vite:preloadError`.
 *
 * The previous handler was `() => window.location.reload()`, unconditional and
 * unbounded. When the reload is served the *same* stale shell — the service
 * worker replaying its cached `index.html`, or a stale CDN edge — the fresh page
 * requests the same missing chunk, fires the same event, and reloads again.
 * That is an infinite reload loop, and it was observed live on
 * ops.avenuecareers.com/cohorts.
 *
 * ─── THE LADDER ───
 *
 * Attempts are counted in `sessionStorage` (survives the reload, dies with the
 * tab — `localStorage` would poison every future session in that browser):
 *
 *   attempt 1 → plain reload. Fixes the common case where the CDN/SW already
 *               has the new shell and only this tab was stale.
 *   attempt 2 → escalate. A plain reload is what just failed, so before
 *               reloading again we destroy whatever is serving the stale shell:
 *               unregister every service-worker registration for this scope and
 *               delete every Cache Storage bucket. `registration.update()` is
 *               not enough — the cached `index.html` IS the problem, and an
 *               updated-but-still-controlling worker keeps replaying it. Then
 *               force-revalidate every precached asset, because the HTTP cache
 *               can hold a poisoned entry that no purge reaches — see
 *               `repairAssetHttpCache` for the measurement behind that.
 *   attempt 3+ → stop reloading. No third reload, ever. Run the same purge +
 *               repair once (it cannot loop — this path never reloads), then
 *               surface a persistent toast with a Reload button, so the user
 *               gets a visible, one-tap dead end instead of a blank page or a
 *               spinning tab.
 *
 * Termination is therefore guaranteed by construction: the only two code paths
 * that call `reload()` are gated on a counter that is incremented before the
 * reload and read from storage that survives it.
 */

export const CHUNK_RECOVERY_STORAGE_KEY = "ave:preload-recovery-attempts";

/**
 * Marks that the terminal rung has already purged + repaired in this tab.
 *
 * The purge unregisters the service worker and deletes the precache, and the app
 * only re-registers and re-precaches on the next load — so repeating it on every
 * failed navigation would re-download the precache each time, which on mobile
 * data is a real cost paid in an already-broken state. One repair fixes a
 * poisoned entry; a second changes nothing. Session-scoped like the counter.
 */
export const CHUNK_RECOVERY_REPAIRED_KEY = "ave:preload-repair-done";

/** Hard ceiling on reloads triggered by preload errors, per tab. */
export const MAX_CHUNK_RECOVERY_RELOADS = 2;

/**
 * How long the app must run without a preload error before the attempt counter
 * is reset. See `armSuccessReset` for why it is a delay and not the `load`
 * event.
 */
export const CHUNK_RECOVERY_RESET_DELAY_MS = 15_000;

/** The slice of the `Storage` API this module needs, so tests can fake it. */
export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface ChunkRecoveryEnv {
  /** Normally `window.sessionStorage`. May throw — see `readAttempts`. */
  storage: StorageLike;
  /** Normally `() => window.location.reload()`. */
  reload: () => void;
  /** Unregister service workers + delete Cache Storage. See `purgeStaleShell`. */
  purgeStaleShell: () => Promise<void>;
  /**
   * Force-revalidate precached assets so a poisoned HTTP cache entry is
   * overwritten. See `repairAssetHttpCache` for why Cache Storage is not enough.
   */
  repairAssetHttpCache: () => Promise<number>;
  /** Terminal, user-visible dead end (a persistent `toast.error`). */
  onGiveUp: () => void;
  /** Optional breadcrumb sink (Sentry / console). */
  onDiagnostic?: (message: string, detail?: unknown) => void;
}

/**
 * Read the attempt count. A `sessionStorage` that throws (Safari private mode,
 * storage disabled by policy) or holds garbage is reported as "already at the
 * ceiling": if we cannot count reloads we must not perform any, because an
 * uncounted reload is exactly the unbounded loop this module exists to prevent.
 */
export function readAttempts(storage: StorageLike): number {
  let raw: string | null;
  try {
    raw = storage.getItem(CHUNK_RECOVERY_STORAGE_KEY);
  } catch {
    return MAX_CHUNK_RECOVERY_RELOADS;
  }
  if (raw === null) return 0;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return MAX_CHUNK_RECOVERY_RELOADS;
  return Math.min(parsed, MAX_CHUNK_RECOVERY_RELOADS);
}

function writeAttempts(storage: StorageLike, attempts: number): boolean {
  try {
    storage.setItem(CHUNK_RECOVERY_STORAGE_KEY, String(attempts));
    return true;
  } catch {
    // Cannot persist the increment, so the next load would start from zero and
    // loop forever. Refuse to reload instead.
    return false;
  }
}

/**
 * Has the terminal rung already purged + repaired in this tab?
 *
 * A storage that throws answers "no": the cap is an optimisation, and losing it
 * costs one extra repair per failed navigation, whereas wrongly answering "yes"
 * would skip the repair that is the whole point of the rung.
 */
export function alreadyRepaired(storage: StorageLike): boolean {
  try {
    return storage.getItem(CHUNK_RECOVERY_REPAIRED_KEY) !== null;
  } catch {
    return false;
  }
}

export function markRepaired(storage: StorageLike): void {
  try {
    storage.setItem(CHUNK_RECOVERY_REPAIRED_KEY, "1");
  } catch {
    // Cap lost, repair still runs. See `alreadyRepaired`.
  }
}

export function clearAttempts(storage: StorageLike): void {
  try {
    storage.removeItem(CHUNK_RECOVERY_STORAGE_KEY);
    storage.removeItem(CHUNK_RECOVERY_REPAIRED_KEY);
  } catch {
    // Nothing to do — a counter we cannot clear only costs the next genuine
    // failure its retries within this same tab.
  }
}

/**
 * Destroy every local copy of the shell: unregister the service worker(s) so a
 * cached `index.html` can no longer be replayed, then drop all Cache Storage
 * buckets (workbox precache included). Feature-detected, and each half is
 * independently best-effort — a browser without `caches` still gets the
 * unregister, and a rejected delete never blocks the reload that follows.
 */
export async function purgeStaleShell(): Promise<void> {
  const jobs: Promise<unknown>[] = [];

  if (typeof navigator !== "undefined" && navigator.serviceWorker) {
    jobs.push(
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(registrations.map((r) => r.unregister())),
        )
        .catch(() => undefined),
    );
  }

  if (typeof caches !== "undefined") {
    jobs.push(
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
        .catch(() => undefined),
    );
  }

  await Promise.all(jobs);
}

/**
 * ─── WHY PURGING THE SERVICE WORKER IS NOT ENOUGH ───
 *
 * `purgeStaleShell` clears Cache Storage. The failure Lisa hit on 2026-07-31
 * lived in the *HTTP* cache, which no JavaScript API can clear — and it was
 * poisoned with a one-year `immutable` lifetime, so no reload would ever
 * re-request the URL. Measured against the live deployment that day:
 *
 *   $ curl -sI https://ops.avenuecareers.com/assets/clients-DOESNOTEXIST.js
 *   HTTP/2 200
 *   content-type: text/html; charset=utf-8
 *   cache-control: public, max-age=31536000, immutable
 *
 * Cloudflare Pages answers a missing asset with the SPA fallback — index.html,
 * status **200** — and the `/assets/*` rule in `public/_headers` stamps that
 * HTML `immutable` for a year. So any request for a chunk that the edge did not
 * have yet (a deploy racing an open tab) leaves the browser holding an HTML body
 * permanently cached at a `.js` URL. Every later load of the *correct* shell
 * asks for that same hash, gets HTML from its own disk cache, and throws
 * `'text/html' is not a valid JavaScript MIME type` — for a year, on a route
 * whose chunk hash only changes when that route's source changes. Reload does
 * nothing. Purging the service worker does nothing. On a phone there is no
 * hard-reload shortcut at all, which is why this repair exists.
 *
 * `fetch(url, { cache: "reload" })` is the escape hatch: it bypasses the HTTP
 * cache on the way out *and overwrites the stored entry* with the response, so
 * one forced fetch per asset replaces the poisoned HTML with the real JavaScript
 * the edge now serves. We do not know which chunk failed — Vite's
 * `vite:preloadError` payload carries an Error whose message omits the URL in
 * exactly the browsers that report the MIME variant — so instead of guessing we
 * repair every asset the deployment declares.
 *
 * The list comes from `/sw.js`, which contains the precache manifest verbatim
 * (`{url:"assets/clients-DFk8jMS7.js",revision:null}`) and is the one file
 * `public/_headers` pins to `max-age=0, must-revalidate`, so a fresh copy is
 * always one request away.
 *
 * Ordering matters: run this AFTER `purgeStaleShell`. A controlling service
 * worker intercepts `fetch` before the network, and `cache: "reload"` says
 * nothing about Cache Storage — with the buckets already deleted, workbox's
 * precache lookup misses and falls through to the network as required.
 *
 * Every failure mode here is non-fatal by construction: this runs immediately
 * before a reload that would happen anyway, so an unreachable `/sw.js`, an
 * unparseable manifest, or a rejected fetch costs nothing but the repair.
 */

/** Cap on assets force-revalidated in one repair pass. */
export const MAX_ASSET_REPAIRS = 60;

/**
 * Pull precached asset URLs out of a workbox service-worker source.
 *
 * Matches both the minified (`url:"…"`) and quoted (`"url":"…"`) manifest forms,
 * and keeps only the script/style assets a preload error can be about — icons
 * and the HTML shell are not what strands a lazy route.
 */
export function parsePrecachedAssetUrls(swSource: string): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();
  const pattern = /["']?url["']?\s*:\s*["']([^"']+)["']/g;

  for (const match of swSource.matchAll(pattern)) {
    const url = match[1];
    if (!/\.(?:js|css)$/.test(url)) continue;
    if (seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
    if (urls.length >= MAX_ASSET_REPAIRS) break;
  }

  return urls;
}

/**
 * Force-revalidate every precached script/style so a poisoned HTTP cache entry
 * is overwritten with what the edge serves now. Resolves to the number of assets
 * successfully re-fetched (0 when nothing could be repaired).
 */
export async function repairAssetHttpCache(): Promise<number> {
  if (typeof fetch !== "function") return 0;

  let source: string;
  try {
    const response = await fetch("/sw.js", { cache: "reload" });
    if (!response.ok) return 0;
    source = await response.text();
  } catch {
    return 0;
  }

  const urls = parsePrecachedAssetUrls(source);
  if (urls.length === 0) return 0;

  const results = await Promise.all(
    urls.map((url) =>
      fetch(url.startsWith("/") ? url : `/${url}`, { cache: "reload" })
        .then((response) => response.ok)
        .catch(() => false),
    ),
  );

  return results.filter(Boolean).length;
}

export interface ChunkRecovery {
  /** Handle one `vite:preloadError`. Resolves once the decision is acted on. */
  handlePreloadError: () => Promise<void>;
  /**
   * Start the "we survived, forget the failure" timer. Returns a cancel fn.
   */
  armSuccessReset: (delayMs?: number) => () => void;
}

export function createChunkRecovery(env: ChunkRecoveryEnv): ChunkRecovery {
  const {
    storage,
    reload,
    purgeStaleShell: purge,
    repairAssetHttpCache: repair,
    onGiveUp,
  } = env;
  const diag = env.onDiagnostic ?? (() => undefined);

  /**
   * Purge the stale shell, then overwrite any poisoned HTTP cache entries.
   * Order is load-bearing — see the `repairAssetHttpCache` header.
   */
  const cleanLocalCopies = async (): Promise<void> => {
    await purge();
    const repaired = await repair();
    diag(`force-revalidated ${repaired} precached asset(s)`);
  };

  // One decision per page load. A stale shell usually strands several lazy
  // chunks at once, and Vite fires the event once per failed chunk; without
  // this, three simultaneous failures would burn all three rungs of the ladder
  // in a single load.
  let handled = false;
  let cancelReset: (() => void) | undefined;

  const handlePreloadError = async (): Promise<void> => {
    if (handled) return;
    handled = true;
    // The app did NOT come up clean, so the success reset must not fire.
    cancelReset?.();

    const attempts = readAttempts(storage);

    if (attempts >= MAX_CHUNK_RECOVERY_RELOADS) {
      diag(
        `chunk preload failed after ${attempts} recovery reload(s) — giving up`,
      );
      // Repair before surfacing the dead end, even though we will not reload.
      // A tab whose counter is already spent (an installed PWA window lives for
      // days, and sessionStorage lives exactly as long) would otherwise reach
      // the terminal toast without ever having overwritten the poisoned cache
      // entry — which is precisely the state that makes the toast's advice the
      // only way out. This path never calls `reload`, so it cannot loop. It is
      // capped at once per tab, because the purge costs a full precache
      // re-download on the next load and a second repair fixes nothing.
      if (!alreadyRepaired(storage)) {
        markRepaired(storage);
        await cleanLocalCopies();
      } else {
        diag("terminal rung already purged + repaired in this tab — skipping");
      }
      onGiveUp();
      return;
    }

    const next = attempts + 1;
    if (!writeAttempts(storage, next)) {
      diag("cannot persist chunk-recovery attempt count — giving up");
      onGiveUp();
      return;
    }

    if (next > 1) {
      // A plain reload already failed, so a local copy is stale. Clear
      // everything that can serve one before trying again.
      diag(`chunk preload retry ${next}: purging service worker + caches`);
      await cleanLocalCopies();
    } else {
      diag("chunk preload failed: reloading once");
    }

    reload();
  };

  const armSuccessReset = (
    delayMs: number = CHUNK_RECOVERY_RESET_DELAY_MS,
  ): (() => void) => {
    // Why a delay and not `window.load`: the failure this module recovers from
    // surfaces *after* the document loads — the shell and entry chunk are fine,
    // it is the first lazy route import that 404s. Clearing on `load` would
    // reset the counter before the error that needs counting, restoring the
    // infinite loop. Clearing after the app has run for a stretch with no
    // preload error means the shell and the chunks it references are consistent,
    // which is the actual success condition. Session-scoped either way, so the
    // cost of being wrong is one extra retry.
    const timer = setTimeout(() => {
      if (handled) return;
      clearAttempts(storage);
      diag("no preload error after startup — chunk-recovery counter cleared");
    }, delayMs);

    cancelReset = () => clearTimeout(timer);
    return cancelReset;
  };

  return { handlePreloadError, armSuccessReset };
}

/**
 * Wire the recovery ladder to the real browser globals. Each app supplies only
 * its own terminal message, so the copy can name the app.
 */
export function installChunkRecovery(options: {
  onGiveUp: () => void;
  onDiagnostic?: (message: string, detail?: unknown) => void;
  /** Override the success-reset delay. Exists so tests need not wait 15s. */
  resetDelayMs?: number;
}): ChunkRecovery {
  const recovery = createChunkRecovery({
    storage: window.sessionStorage,
    reload: () => window.location.reload(),
    purgeStaleShell,
    repairAssetHttpCache,
    onGiveUp: options.onGiveUp,
    onDiagnostic:
      options.onDiagnostic ??
      ((message, detail) => console.warn(`[pwa] ${message}`, detail ?? "")),
  });

  window.addEventListener("vite:preloadError", () => {
    void recovery.handlePreloadError();
  });

  recovery.armSuccessReset(options.resetDelayMs);

  return recovery;
}
